import { useState, useRef, useContext, useEffect } from "react";
import { MessageSquare, Calendar } from "lucide-react";
import { priorityCfg, progressColor } from "./data";
import { Avatar, Badge } from "./helpers";
import { PopupCtx } from "./PopupCtx";
import axiosInstance from "../../API/interceptor";
import { PhotoAvatar } from "./PersonCell";


/* ─────────────────────────────────────────
   PersonTasksPopup
   — workspace breakdown popup (live API)
───────────────────────────────────────── */
function PersonTasksPopup({ name, raciLeft, raciWidth, raciTop, cancelHide, scheduleHide, activeRaci }) {
  const raciLabelMap = { R:"Responsible", A:"Accountable", C:"Consulted", I:"Informed" };

  const [byWorkspace, setByWorkspace] = useState([]);
  const [totalCount,  setTotalCount]  = useState(0);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    if (!name || !activeRaci) return;
    setLoading(true);
    axiosInstance
      .post(`/task_assignment/get_person_product_raci_breakdown`, { person_name: name, raci_role: activeRaci })
      .then((res) => {
        const data = res.data?.breakdown || [];
        setByWorkspace(data);
        setTotalCount(data.reduce((sum, w) => sum + (w.count || 0), 0));
      })
      .catch(() => { setByWorkspace([]); setTotalCount(0); })
      .finally(() => setLoading(false));
  }, [name, activeRaci]);

  const workspaceColors = [
    { color:"#7C3AED", bg:"#F5F3FF", border:"#DDD6FE" },
    { color:"#0D9488", bg:"#F0FDFA", border:"#99F6E4" },
    { color:"#2563EB", bg:"#EFF6FF", border:"#BFDBFE" },
    { color:"#D97706", bg:"#FFF7ED", border:"#FDE68A" },
    { color:"#DC2626", bg:"#FEF2F2", border:"#FECACA" },
    { color:"#DB2777", bg:"#FDF2F8", border:"#F9A8D4" },
  ];

  const POPUP_W=296, GAP=12;
  const spaceRight = window.innerWidth-(raciLeft+raciWidth+GAP);
  const left = spaceRight>=POPUP_W ? raciLeft+raciWidth+GAP : Math.max(8,raciLeft-POPUP_W-GAP);
  const top = Math.min(raciTop, Math.max(8,window.innerHeight-380));

  return (
    <div
      onMouseEnter={cancelHide}
      onMouseLeave={scheduleHide}
      className="fixed z-[2147483647] bg-white rounded-2xl border border-[#E8E4F8] overflow-hidden shadow-[0_24px_64px_rgba(109,40,217,.2),0_4px_16px_rgba(0,0,0,.1)] w-[296px]"
      style={{ left, top }}
    >
      {/* ── Header ── */}
      <div className="px-4 py-3 bg-gradient-to-br from-[#7C3AED] to-[#6D28D9]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            {/* Avatar */}
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0 bg-white/20 border-2 border-white/35">
              {name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-black text-white">{raciLabelMap[activeRaci]} Tasks — {name}</div>
              <div className="text-[10.5px] text-white/60 mt-0.5">Workspace breakdown</div>
            </div>
          </div>
          {/* Count badge */}
          <div className="px-3 py-1 rounded-full flex items-center gap-1 bg-white/18 border border-white/22">
            <span className="text-xs font-bold text-white">{totalCount} tasks</span>
          </div>
        </div>
        {/* Progress bar strips */}
        <div className="flex gap-1 h-1">
          {byWorkspace.map((w, i) => (
            <div
              key={i}
              className={`rounded-sm min-w-1.5 ${i === 0 ? "bg-white/90" : "bg-white/45"}`}
              style={{ flex: w.count }}
            />
          ))}
        </div>
      </div>

      {/* ── Workspace list ── */}
      <div className="p-2.5 flex flex-col gap-1.5 max-h-64 overflow-y-auto">
        {loading
          ? <p className="text-xs text-slate-400 text-center py-4 m-0">Loading...</p>
          : byWorkspace.length === 0
          ? <p className="text-xs text-slate-400 text-center py-4 m-0">No tasks found</p>
          : byWorkspace.map((w, i) => {
              const wc = workspaceColors[i % workspaceColors.length];
              return (
                <div
                  key={w.workspace || i}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-default transition-transform hover:translate-x-1 border-[1.5px]"
                  style={{ background: wc.bg, borderColor: wc.border }}
                >
                  <div className="w-1 h-10 rounded-sm flex-shrink-0" style={{ background: wc.color }} />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-slate-800">{w.workspace || "Unknown"}</div>
                    <div className="text-[10.5px] text-slate-400 mt-0.5">{w.count} task{w.count > 1 ? "s" : ""} assigned</div>
                  </div>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0"
                    style={{ background: wc.color }}
                  >
                    {w.count}
                  </div>
                </div>
              );
            })
        }
      </div>

      {/* ── Footer ── */}
      <div className="px-3.5 py-2.5 border-t border-gray-100 flex justify-between items-center bg-slate-50">
        <span className="text-xs text-slate-400">Total assigned tasks</span>
        <span className="text-sm font-black text-violet-600">{totalCount}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   PersonRaciPopup
   — hover on person name → R/A/C/I counts (live API)
───────────────────────────────────────── */
function PersonRaciPopup({ name, anchorRect, cancelHide, scheduleHide }) {
  const [showTasks,  setShowTasks]  = useState(false);
  const [activeRaci, setActiveRaci] = useState(null);
  const [raciCounts, setRaciCounts] = useState({ R:0, A:0, C:0, I:0 });
  const [wsCount,    setWsCount]    = useState(0);
  const [loading,    setLoading]    = useState(true);
  const popupRef   = useRef();
  const [popupRect, setPopupRect]  = useState(null);
  const tasksTimer = useRef(null);

  useEffect(() => {
    if (!name) return;
    setLoading(true);
    axiosInstance
      .post(`/task_assignment/get_person_raci_counts`, { person_name: name })
      .then((res) => {
        const d = res.data || {};
        setRaciCounts({ R:d.responsible||0, A:d.accountable||0, C:d.consulted||0, I:d.informed||0 });
        setWsCount(d.workspace_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [name]);

  const raciStats = [
    { key:"R", label:"RESPONSIBLE", color:"#16A34A", accentBorder:"#16A34A", bg:"#F0FDF4", count:raciCounts.R },
    { key:"A", label:"ACCOUNTABLE", color:"#2563EB", accentBorder:"#2563EB", bg:"#EFF6FF", count:raciCounts.A },
    { key:"C", label:"CONSULTED",   color:"#D97706", accentBorder:"#D97706", bg:"#FFFBEB", count:raciCounts.C },
    { key:"I", label:"INFORMED",    color:"#7C3AED", accentBorder:"#7C3AED", bg:"#F5F3FF", count:raciCounts.I },
  ];

  const spaceBelow = window.innerHeight - anchorRect.bottom, above = spaceBelow < 260;
  const left = Math.max(8, Math.min(anchorRect.left + 50, window.innerWidth - 420));
  const top  = above ? anchorRect.top - 6 : anchorRect.bottom + 6;

  const handlePopupEnter = () => { cancelHide(); clearTimeout(tasksTimer.current); if (popupRef.current) { setPopupRect(popupRef.current.getBoundingClientRect()); setShowTasks(true); } };
  const handlePopupLeave = () => { scheduleHide(); tasksTimer.current = setTimeout(() => setShowTasks(false), 280); };
  const handleTasksEnter = () => { cancelHide(); clearTimeout(tasksTimer.current); };
  const handleTasksLeave = () => { scheduleHide(); tasksTimer.current = setTimeout(() => { setShowTasks(false); setActiveRaci(null); }, 280); };

  return (
    <>
      {/* ── Main popup ── */}
      <div
        ref={popupRef}
        onMouseEnter={handlePopupEnter}
        onMouseLeave={handlePopupLeave}
        className={`fixed bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_rgba(109,40,217,.2),0_4px_16px_rgba(0,0,0,.1)] pointer-events-auto overflow-hidden z-[2147483647] w-[400px] ${
          above ? "-translate-y-full" : ""
        }`}
        style={{ left, top }}
      >
        {/* Header */}
        <div className="px-4 py-4 flex items-center justify-between bg-gradient-to-br from-[#7C3AED] to-[#6D28D9]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-black text-white flex-shrink-0 bg-white/25 border-2 border-white/40">
              {name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-base font-black text-white">{name}</div>
              <div className="text-[11px] text-white/65 mt-0.5">Hover card to see workspace breakdown</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/18 border border-white/25">
            <span className="text-xs font-bold text-white">Workspace: {wsCount}</span>
          </div>
        </div>

        {/* RACI grid */}
        <div className="grid grid-cols-4 gap-2.5 p-4">
          {loading
            ? <div className="col-span-4 text-center text-xs text-slate-400 py-4">Loading...</div>
            : raciStats.map(r => (
              <div
                key={r.key}
                onMouseEnter={() => { clearTimeout(tasksTimer.current); cancelHide(); if (popupRef.current) setPopupRect(popupRef.current.getBoundingClientRect()); setActiveRaci(r.key); setShowTasks(true); }}
                onMouseLeave={() => { tasksTimer.current = setTimeout(() => { setShowTasks(false); setActiveRaci(null); }, 280); }}
                className="flex flex-col items-center justify-center py-4 px-2 rounded-2xl cursor-pointer relative overflow-hidden transition-all duration-150"
                style={{ background: activeRaci === r.key ? r.accentBorder + "22" : r.bg, border: `2px solid ${r.accentBorder}`, transform: activeRaci === r.key ? "scale(1.06)" : "scale(1)", boxShadow: activeRaci === r.key ? `0 6px 18px ${r.accentBorder}44` : "none" }}
              >
                <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: r.accentBorder }} />
                <span className="text-xl font-black text-gray-700 mb-1.5 mt-1">{r.key}</span>
                <span className="text-3xl font-black leading-none mb-2" style={{ color: r.color }}>{r.count}</span>
                <span className="text-[9px] font-bold tracking-widest text-center opacity-80" style={{ color: r.color }}>{r.label}</span>
              </div>
            ))
          }
        </div>

        <div className="px-4 pb-3 flex items-center justify-center">
          <span className="text-xs text-slate-400">Hover this card to see workspace breakdown</span>
        </div>
      </div>

      {/* ── Tasks popup ── */}
      {showTasks && activeRaci && popupRect && (
        <PersonTasksPopup
          name={name}
          activeRaci={activeRaci}
          raciLeft={left}
          raciWidth={400}
          raciTop={popupRect.top}
          cancelHide={handleTasksEnter}
          scheduleHide={handleTasksLeave}
        />
      )}
    </>
  );
}

/* ─────────────────────────────────────────
   PersonCell
───────────────────────────────────────── */
export function PersonCell({ name, idx, size = 24 }) {
  const [visible, setVisible] = useState(false), [rect, setRect] = useState(null);
  const ref = useRef(), hideTimer = useRef(null), openTimer = useRef(null);
  const cancelHide  = () => clearTimeout(hideTimer.current);
  const scheduleHide = () => {
    clearTimeout(openTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), 300);
  };
  const handleEnter = () => {
    cancelHide();
    clearTimeout(openTimer.current);
    if (ref.current) {
      const nextRect = ref.current.getBoundingClientRect();
      openTimer.current = setTimeout(() => {
        setRect(nextRect);
        setVisible(true);
      }, 500);
    }
  };
  return (
    <div ref={ref} className="flex items-center gap-2 cursor-pointer" onMouseEnter={handleEnter} onMouseLeave={scheduleHide}>
      <Avatar name={name} idx={idx} size={size} />
      <span className="text-xs text-gray-700 whitespace-nowrap">{name}</span>
      {visible && rect && <PersonRaciPopup name={name} anchorRect={rect} cancelHide={cancelHide} scheduleHide={scheduleHide} />}
    </div>
  );
}

/* ─────────────────────────────────────────
   ListViewRaciModal
   — used in ListView.js on task name hover
   — receives raw API row data directly
───────────────────────────────────────── */
export function ListViewRaciModal({ row, pos, onEnter, onLeave }) {
  if (!row) return null;

  const MoreNames = ({ names = [], ids = [], color, bg, border }) => {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const ref = useRef(null);
    const timer = useRef(null);

    const firstName = names[0];
    const firstId   = ids[0];
    const extra     = names.slice(1);
    const extraIds  = ids.slice(1);

    const cancel    = () => clearTimeout(timer.current);
    const closeLater = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 150); };

    if (names.length === 0) return <span className={'text-[13px] text-slate-300 font-semibold'}>{'—'}</span>;

    return (
      <div className="flex items-center gap-[5px] flex-nowrap">
        {/* first visible person with photo */}
        <div className="flex items-center gap-1.5 min-w-0">
          <PhotoAvatar name={firstName} userId={firstId} size={20} />
          <span className="text-[11px] text-gray-700 font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[72px]">
            {firstName?.split(" ")[0]}
          </span>
        </div>

        {/* +N pill inline */}
        {extra.length > 0 && (
          <>
            <div
              ref={ref}
              onMouseEnter={() => { cancel(); if (ref.current) setRect(ref.current.getBoundingClientRect()); setOpen(true); }}
              onMouseLeave={closeLater}
              className="inline-flex items-center justify-center w-[20px] h-[20px] rounded-full text-[9px] font-bold cursor-default select-none flex-shrink-0 transition-all duration-150 border"
              style={{ background: open ? color : bg, color: open ? "#fff" : color, borderColor: border }}
            >
              +{extra.length}
            </div>

            {open && rect && (
              <div
                onMouseEnter={cancel}
                onMouseLeave={closeLater}
                className="fixed z-[9999] bg-white border border-slate-200 rounded-xl overflow-hidden shadow-[0_8px_28px_rgba(99,102,241,0.18),0_2px_8px_rgba(0,0,0,0.1)] min-w-[160px]"
                style={{ left: Math.min(rect.right + 6, window.innerWidth - 190), top: rect.top - 4 }}
              >
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    {extra.length} more
                  </span>
                </div>
                <div className="p-2 flex flex-col gap-1.5">
                  {extra.map((n, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <PhotoAvatar name={n} userId={extraIds[i]} size={20} />
                      <span className="text-[11px] text-gray-700 font-medium">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const getNames = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return [val];
  };

  const responsible    = getNames(row.assigned_to_name);
  const accountable    = row.task_created_by || null;
  const consultedNames = getNames(row.observer_to_name);
  const informedNames  = getNames(row.informer_to_name);

  const responsibleIds = Array.isArray(row.assigned_to_ids) ? row.assigned_to_ids : [];
  const consultedIds   = Array.isArray(row.observer_ids)    ? row.observer_ids    : [];
  const informedIds    = Array.isArray(row.informer_ids)    ? row.informer_ids    : [];

  const priority = (row.priority || "MEDIUM").toUpperCase();
  const pc       = priorityCfg[priority] || priorityCfg["MEDIUM"];

  const STATUS_MAP = { "open":"open","in progress":"in-progress","in-progress":"in-progress","on hold":"on-hold","on-hold":"on-hold","completed":"complete","complete":"complete","approval pending":"approval pending","closed":"closed" };
  const status    = STATUS_MAP[(row.task_status || "").toLowerCase()] || "open";
  const pColor    = progressColor[status] || "#7C3AED";
  const progress  = status === "complete" ? 100 : status === "in-progress" ? 50 : 0;

  const sla    = row.completion_sla ? new Date(row.completion_sla) : null;
  const overdue = sla && sla < new Date() && !["complete","closed"].includes(status);
  const fmtDl = (d) => {
    if (!d) return null;
    const dt = new Date(d); if (isNaN(dt)) return null;
    const dd = String(dt.getDate()).padStart(2,"0"), mm = String(dt.getMonth()+1).padStart(2,"0");
    const h24 = dt.getHours(), h12 = h24%12||12, ampm = h24>=12?"PM":"AM";
    return `${dd}-${mm}-${dt.getFullYear()} ${String(h12).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")} ${ampm}`;
  };

  const raciCols = [
    { key:"R", label:"Responsible", color:"#7C3AED", bg:"#F5F3FF", border:"#DDD6FE", multi:true,  names: responsible,    ids: responsibleIds },
    { key:"A", label:"Accountable",  color:"#0D9488", bg:"#F0FDFA", border:"#99F6E4", multi:false, value: accountable,    valueId: row.task_created_by_id },
    { key:"C", label:"Consulted",    color:"#D97706", bg:"#FFF7ED", border:"#FDE68A", multi:true,  names: consultedNames, ids: consultedIds },
    { key:"I", label:"Informed",     color:"#DB2777", bg:"#FDF2F8", border:"#F9A8D4", multi:true,  names: informedNames,  ids: informedIds },
  ];

  const modalW = 480;
  const left   = Math.max(8, Math.min(pos.x + 50, window.innerWidth - modalW - 16));
  const above  = window.innerHeight - pos.y < 240;
  const top    = above ? pos.y - 8 : pos.y + 8;

  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={`fixed bg-white border border-slate-100 rounded-2xl shadow-[0_25px_60px_rgba(109,40,217,.25),0_8px_24px_rgba(0,0,0,.12)] overflow-hidden pointer-events-auto z-[2147483647] w-[480px] ${
        above ? "-translate-y-full" : ""
      }`}
      style={{ left, top }}
    >
      {/* ── Header ── */}
      <div className="px-4 py-3 flex items-center gap-3 bg-gradient-to-br from-[#7C3AED] to-[#6D28D9]">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/20">
          <MessageSquare size={13} color="#fff" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white overflow-hidden text-ellipsis whitespace-nowrap">{row.task_type}</div>
          <div className="text-[10.5px] text-white/70 mt-0.5">#{row.unique_seq_id} · {row.product_name}</div>
        </div>
        {/* Priority badge */}
        <span
          className="inline-flex items-center gap-1 text-[11px] font-bold py-1 px-2.5 rounded-lg flex-shrink-0 border"
          style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}
        >
          <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: pc.dot }} />
          {priority}
        </span>
      </div>


      {/* ── RACI 4-col grid ── */}
      <div className="grid grid-cols-4 border-b border-slate-100">
        {raciCols.map(col => (
          <div key={col.key} className={`p-3 ${col.key !== "I" ? "border-r border-slate-100" : ""}`}>
            {/* Key badge */}
            <div
              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-xs font-black mb-1.5 border"
              style={{ background: col.bg, borderColor: col.border, color: col.color }}
            >
              {col.key}
            </div>
            {/* Label */}
            <div className="text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-[.06em]">{col.label}</div>

            {col.multi
              ? col.names.length > 0
                ? <MoreNames names={col.names} ids={col.ids} color={col.color} bg={col.bg} border={col.border} />
                : <span className="text-[13px] text-slate-300 font-semibold">—</span>
              : col.value
                ? <div className="flex items-center gap-1.5">
                    <PhotoAvatar name={col.value} userId={col.valueId} size={20} />
                    <span className="text-[11px] text-gray-700 font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[72px]">
                      {col.value.split(" ")[0]}
                    </span>
                  </div>
                : <span className="text-[13px] text-slate-300 font-semibold">—</span>
            }
          </div>
        ))}
      </div>

      {/* ── Footer ── */}
      <div className="px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <Calendar size={12} color={overdue ? "#DC2626" : "#16A34A"} />
          <span className={`text-xs font-semibold ${overdue ? "text-red-600" : "text-green-600"}`}>
            {fmtDl(row.completion_sla) || "No Deadline"}
          </span>
          {overdue && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">
              Overdue
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-1 max-w-36">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${progress}%`, background: pColor }} />
          </div>
          <span className="text-[11px] font-bold flex-shrink-0" style={{ color: pColor }}>{progress}%</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   RaciPopover — default export (CardView use)
───────────────────────────────────────── */
function MoreNamesPill({ names = [], color, bg, border }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const timer = useRef(null);

  const visible = names.slice(0, 1);
  const extra = names.slice(1);

  const cancel = () => clearTimeout(timer.current);
  const closeLater = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  if (names.length === 0) return <span className="text-[11px] text-slate-300">â€"</span>;

  return (
    <div className="relative inline-flex flex-col gap-[3px]">
      {visible.map((n, idx) => (
        <div key={idx} className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0" style={{ background: color }}>
            {n.charAt(0).toUpperCase()}
          </div>
          <span className="text-[11px] text-gray-700 font-medium">{n}</span>
        </div>
      ))}

      {extra.length > 0 && (
        <>
          <div
            ref={ref}
            onMouseEnter={() => {
              cancel();
              if (ref.current) setRect(ref.current.getBoundingClientRect());
              setOpen(true);
            }}
            onMouseLeave={closeLater}
            className="inline-flex items-center self-start gap-1 px-1.5 py-[2px] rounded-md text-[10px] font-bold cursor-default select-none border"
            style={{ background: bg, color, borderColor: border }}
          >
            +{extra.length} more
          </div>

          {open && rect && (
            <div
              onMouseEnter={cancel}
              onMouseLeave={closeLater}
              className="fixed z-[9999] bg-white border border-slate-200 rounded-xl overflow-hidden shadow-[0_8px_28px_rgba(99,102,241,0.18),0_2px_8px_rgba(0,0,0,0.1)] min-w-[170px]"
              style={{ left: Math.min(rect.right + 8, window.innerWidth - 190), top: rect.top }}
            >
              <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  {extra.length} more name{extra.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="p-2 flex flex-col gap-1.5">
                {extra.map((n, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0" style={{ background: color }}>
                      {n.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-[11px] text-gray-700 font-medium">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
export default function RaciPopover({ task }) {
  const { setPopup, setCommentTask, setDetailTask } = useContext(PopupCtx);
  const ref = useRef(), hideTimer = useRef(null);
  const openTimer = useRef(null);
  const consultedNames = Array.isArray(task.consulted) ? task.consulted : [];
  const informedNames  = Array.isArray(task.informed)  ? task.informed  : [];
  const raciCols = [
    { key:"R", label:"Responsible", color:"#7C3AED", bg:"#F5F3FF", border:"#DDD6FE", value:task.responsible,         multi:false },
    { key:"A", label:"Accountable",  color:"#0D9488", bg:"#F0FDFA", border:"#99F6E4", value:task.accountable,         multi:false },
    { key:"C", label:"Consulted",    color:"#D97706", bg:"#FFF7ED", border:"#FDE68A", value:consultedNames[0]||null,  multi:true, names:consultedNames },
    { key:"I", label:"Informed",     color:"#DB2777", bg:"#FDF2F8", border:"#F9A8D4", value:informedNames[0]||null,   multi:true, names:informedNames },
  ];
  const scheduleHide = () => {
    clearTimeout(openTimer.current);
    hideTimer.current = setTimeout(() => setPopup(null), 200);
  };
  const cancelHide   = () => clearTimeout(hideTimer.current);
  const handleEnter  = () => {
    cancelHide();
    const rect  = ref.current.getBoundingClientRect();
    const above = window.innerHeight - rect.bottom < 220;
    const x     = Math.max(8, Math.min(rect.left + 50, window.innerWidth - 490));
    const y     = above ? rect.top - 8 : rect.bottom + 8;
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setPopup(
      <div
        onMouseEnter={cancelHide}
        onMouseLeave={scheduleHide}
        className={`fixed bg-white border border-slate-100 rounded-2xl shadow-[0_25px_60px_rgba(109,40,217,.25),0_8px_24px_rgba(0,0,0,.12)] overflow-hidden z-[2147483647] w-[480px] ${
          above ? "-translate-y-full" : ""
        }`}
        style={{ left: x, top: y }}
      >
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-3 bg-gradient-to-br from-[#7C3AED] to-[#6D28D9]">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/20">
            <MessageSquare size={13} color="#fff" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white overflow-hidden text-ellipsis whitespace-nowrap">{task.name}</div>
            <div className="text-[10.5px] text-white/70 mt-0.5">#{task.id} · {task.workspace}</div>
          </div>
          <Badge cfg={priorityCfg[task.priority]} label={task.priority} small />
        </div>

        {/* RACI grid */}
        <div className="grid grid-cols-4 border-b border-slate-100">
          {raciCols.map(col => (
            <div key={col.key} className={`p-3 ${col.key !== "I" ? "border-r border-slate-100" : ""}`}>
              {/* Key badge */}
              <div
                className="inline-flex items-center justify-center w-6 h-6 rounded-md text-xs font-black mb-1.5 border"
                style={{ background: col.bg, borderColor: col.border, color: col.color }}
              >
                {col.key}
              </div>
              {/* Label */}
              <div className="text-[10px] font-bold text-slate-400 mb-1 uppercase tracking-[.06em]">{col.label}</div>

              {col.multi
                ? col.names && col.names.length > 0
                  ? <MoreNamesPill names={col.names} color={col.color} bg={col.bg} border={col.border} />
                  : <span className="text-[11px] text-slate-300">—</span>
                : col.value
                  ? <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0" style={{ background: col.color }}>
                        {col.value.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-[11px] text-gray-700 font-medium">{col.value}</span>
                    </div>
                  : <span className="text-[11px] text-slate-300">—</span>
              }
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <Calendar size={12} color={task.overdue ? "#DC2626" : "#16A34A"} />
            <span className={`text-xs font-semibold ${task.overdue ? "text-red-600" : "text-green-600"}`}>
              {task.deadline}
            </span>
            {task.overdue && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">
                Overdue
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-1 max-w-36">
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${task.progress}%`, background: progressColor[task.status] }} />
            </div>
            <span className="text-[11px] font-bold flex-shrink-0" style={{ color: progressColor[task.status] }}>
              {task.progress}%
            </span>
          </div>
        </div>
      </div>
    ), 500);
  };

  return (
    <span ref={ref} className="inline-flex items-center gap-1.5">
      <span onClick={e => { e.stopPropagation(); setCommentTask(task); }} className="cursor-pointer flex-shrink-0" title="View Comments">
        <MessageSquare size={13} color="#A78BFA" className="hover:scale-110 transition-transform" />
      </span>
      <span
        onMouseEnter={handleEnter}
        onMouseLeave={scheduleHide}
        onClick={e => { e.stopPropagation(); setDetailTask(task); setPopup(null); }}
        className="text-violet-600 font-semibold text-sm cursor-pointer hover:underline [word-break:break-word] leading-[1.4]"
      >
        {task.name}
      </span>
    </span>
  );
}